<script setup>
import { computed } from 'vue'

const { buildingParts = [], selectedBuildingPart = null, buildingPartsListExpanded = true } = defineProps({
  buildingParts: Array,
  selectedBuildingPart: Object,
  buildingPartsListExpanded: Boolean
})

const emit = defineEmits(['select', 'toggle-expand'])

const selectBuildingPartAndCollapse = (part) => {
  emit('select', part)
}

// Identify a part by its globally-unique adsOid, falling back to the apartment
// number only when a part has none. Buildings spanning sub-addresses (Aiandi tn 7
// → 7/1, 7/2, 7/3) repeat unit numbers, so comparing by number alone highlighted
// all three "Korter 14" rows and gave them colliding :keys.
const partKey = (part) => part.adsOid ?? part['ehitise-osa-number']

const isSelected = (part) =>
  Boolean(selectedBuildingPart) && partKey(selectedBuildingPart) === partKey(part)
</script>

<template>
  <div class="building-part-selector" data-testid="qv-building-parts">
    <!-- Selected Building Part Display (collapsed state) -->
    <Transition name="slide-fade">
      <div
        v-if="selectedBuildingPart"
        class="group"
      >
        <!-- Selected part card -->
        <div
          class="p-4 sm:p-5 border-2 border-emerald-500 bg-linear-to-br from-emerald-50 to-emerald-50/50 rounded-xl cursor-pointer transition-all duration-200 hover:bg-emerald-100 hover:border-emerald-600 hover:shadow-md"
          @click="emit('toggle-expand', !buildingPartsListExpanded)"
          role="button"
          tabindex="0"
          @keydown.enter="emit('toggle-expand', !buildingPartsListExpanded)"
          @keydown.space.prevent="emit('toggle-expand', !buildingPartsListExpanded)"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2.5 mb-3">
                <span class="pi pi-check-circle text-emerald-600 text-xl shrink-0"></span>
                <span class="font-semibold text-slate-900 text-lg">
                  {{ $t('messages.quickValuation.apartment') }} {{ selectedBuildingPart['ehitise-osa-number'] }}
                </span>
              </div>
              <div class="flex flex-wrap gap-2 text-sm">
                <span class="px-3 py-1.5 rounded-full bg-white/90 text-slate-700 font-medium shadow-xs">
                  {{ selectedBuildingPart['ehitise-osa-pind'] }} {{ $t('messages.quickValuation.squareMeters') }}
                </span>
                <span class="px-3 py-1.5 rounded-full bg-white/90 text-slate-700 shadow-xs">
                  {{ selectedBuildingPart['ehitise-osa-tubade-arv'] }} {{ $t('messages.quickValuation.rooms') }}
                </span>
                <span class="px-3 py-1.5 rounded-full bg-white/90 text-slate-700 shadow-xs">
                  {{ selectedBuildingPart['ehitise-osa-korrus'] }}. {{ $t('messages.quickValuation.floor') }}
                </span>
                <span v-if="selectedBuildingPart.adsOid" class="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 font-medium shadow-xs">
                  {{ selectedBuildingPart.adsOid }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-1.5 text-slate-400 group-hover:text-emerald-600 transition-colors ml-2 shrink-0">
              <span class="text-xs font-medium hidden sm:inline">{{ $t('messages.quickValuation.change') }}</span>
              <span class="pi pi-chevron-down text-sm"></span>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Building Parts List (expanded state) -->
    <div v-if="buildingParts.length > 0 && (!selectedBuildingPart || buildingPartsListExpanded)">
      <div class="mt-3 space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar overflow-x-hidden">
        <TransitionGroup name="list">
          <div
            v-for="(part, index) in buildingParts"
            :key="partKey(part)"
            data-testid="qv-building-part"
            class="building-part-item group p-4 sm:p-5 border-2 rounded-xl cursor-pointer transition-all duration-300"
            :class="isSelected(part)
              ? 'border-emerald-500 bg-linear-to-br from-emerald-50 to-emerald-50/30 shadow-lg shadow-emerald-500/10 -translate-y-0.5'
              : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 hover:shadow-md hover:-translate-y-0.5'"
            role="button"
            tabindex="0"
            :style="{ '--animation-order': index }"
            @click="selectBuildingPartAndCollapse(part)"
            @keydown.enter="selectBuildingPartAndCollapse(part)"
            @keydown.space.prevent="selectBuildingPartAndCollapse(part)"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <span
                  v-if="isSelected(part)"
                  class="pi pi-check-circle text-emerald-600 text-xl shrink-0 animate-check-pop"
                ></span>
                <span class="font-semibold text-slate-900 text-base sm:text-lg">
                  {{ $t('messages.quickValuation.apartment') }} {{ part['ehitise-osa-number'] }}
                </span>
              </div>
              <div class="flex flex-wrap gap-2 text-sm shrink-0">
                <span class="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 font-medium shadow-xs transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                  {{ part['ehitise-osa-pind'] }} {{ $t('messages.quickValuation.squareMeters') }}
                </span>
                <span class="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 shadow-xs transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                  {{ part['ehitise-osa-tubade-arv'] }} {{ $t('messages.quickValuation.rooms') }}
                </span>
                <span class="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 shadow-xs transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                  {{ part['ehitise-osa-korrus'] }}. {{ $t('messages.quickValuation.floor') }}
                </span>
                <span v-if="part.adsOid" class="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 font-medium shadow-xs">
                  {{ part.adsOid }}
                </span>
              </div>
              <span
                v-if="!isSelected(part)"
                class="pi pi-arrow-right text-slate-300 ml-2 shrink-0 transition-transform group-hover:translate-x-1 group-hover:text-blue-500"
              ></span>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @reference '../../../css/tailwind.css';

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--color-slate-200);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--color-slate-300);
}

/* Staggered entrance animation for building parts */
.building-part-item {
  animation: slideInFromRight 0.4s ease forwards;
  animation-delay: calc(var(--animation-order, 0) * 60ms);
  opacity: 0;
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Check mark pop animation */
.animate-check-pop {
  animation: checkPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes checkPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
